import { Link } from "react-router-dom";
import { DetailSkeleton } from "../components/Skeleton.jsx";
import {
  WORKER_LINE_STATUS_OPTIONS,
  statusBadgeVariant,
  formatDateTimeTh,
  describeDetailError,
  describeWriteError,
  nextActionLabel,
  isNavigableNextAction,
  fieldLabel,
} from "./workerLineShared.js";

/**
 * Shared Super Agent detail/create/edit drawer views (Prompt 132), reused by
 * both ../hierarchy/SuperAgentsOverviewPage.jsx (the Super Agent list) and
 * ./WorkerLineOrgChartPage.jsx (a Super Agent node's own drawer) - both
 * screens show the exact same real fields over the same
 * GET/POST/PATCH .../worker-line/super-agents API, so one shared component
 * keeps them consistent instead of duplicating the same form twice.
 */

export function SuperAgentDetailView({ titleId, state, onEdit, onCreatePlc, onCreateAgent }) {
  if (state.status === "loading" || state.status === "idle") {
    return (
      <DetailSkeleton label="กำลังโหลดรายละเอียด..." />
    );
  }

  if (state.status === "error") {
    return (
      <div className="wx-wl-drawer-state" role="alert">
        <h2 id={titleId} className="wx-state-title">
          ไม่สามารถโหลดรายละเอียดได้
        </h2>
        <p className="wx-state-body">{describeDetailError(state.error)}</p>
      </div>
    );
  }

  const detail = state.data;
  const plcSummary = detail.plcSummary ?? [];
  const nextActions = detail.nextActions ?? [];

  return (
    <div className="wx-wl-drawer">
      <span className="wx-wl-drawer-kicker">SUPER AGENT · DETAIL</span>
      <h2 id={titleId} className="wx-wl-drawer-title">
        {detail.displayName}
      </h2>
      <p className="wx-wl-drawer-sub">{detail.superAgentCode}</p>
      <div className="wx-wl-drawer-status">
        <span className={`wx-badge wx-badge--${statusBadgeVariant(detail.status)}`}>{detail.status}</span>
      </div>

      <section className="wx-wl-dsec">
        <h3>ข้อมูลทั่วไป</h3>
        <div className="wx-wl-kv-row">
          <span>{fieldLabel("legalName")}</span>
          <strong>{detail.legalName || "-"}</strong>
        </div>
        <div className="wx-wl-kv-row">
          <span>{fieldLabel("ownerName")}</span>
          <strong>{detail.ownerName || "-"}</strong>
        </div>
        {detail.note && (
          <div className="wx-wl-kv-row">
            <span>{fieldLabel("note")}</span>
            <strong>{detail.note}</strong>
          </div>
        )}
        <div className="wx-wl-kv-row">
          <span>{fieldLabel("createdAtUtc")}</span>
          <strong>{formatDateTimeTh(detail.createdAtUtc) ?? "-"}</strong>
        </div>
        <div className="wx-wl-kv-row">
          <span>{fieldLabel("updatedAtUtc")}</span>
          <strong>{formatDateTimeTh(detail.updatedAtUtc) ?? "-"}</strong>
        </div>
      </section>

      <section className="wx-wl-dsec">
        <h3>ผู้ติดต่อ</h3>
        <div className="wx-wl-kv-row">
          <span>{fieldLabel("primaryPhoneNumber")}</span>
          <strong>{detail.primaryPhoneNumber || "-"}</strong>
        </div>
        <div className="wx-wl-kv-row">
          <span>{fieldLabel("lineId")}</span>
          <strong>{detail.lineId || "-"}</strong>
        </div>
      </section>

      <section className="wx-wl-dsec">
        <h3>สรุป PLC / บมจ ({plcSummary.length})</h3>
        {plcSummary.length === 0 ? (
          <p className="wx-empty-list">ยังไม่มี PLC ใต้สายงานนี้</p>
        ) : (
          <div className="wx-wl-summary-list">
            {plcSummary.map((plc) => (
              <div className="wx-wl-summary-row" key={plc.plcOrganizationId}>
                <div className="wx-wl-summary-row-name">
                  <strong>{plc.displayName}</strong>
                  <span>{plc.plcCode}</span>
                </div>
                <span className={`wx-badge wx-badge--${statusBadgeVariant(plc.status)}`}>{plc.status}</span>
                <span className="wx-wl-summary-row-count">
                  <strong>{plc.agentCount}</strong>
                  Agent
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="wx-wl-dsec">
        <h3>จำนวนรวม</h3>
        <div className="wx-wl-counts-grid">
          <div className="wx-wl-count-cell">
            <span>PLC</span>
            <strong>{plcSummary.length}</strong>
          </div>
          <div className="wx-wl-count-cell">
            <span>Agent</span>
            <strong>{detail.agentCount}</strong>
          </div>
          <div className="wx-wl-count-cell">
            <span>แรงงาน</span>
            <strong>{detail.workerCount}</strong>
          </div>
        </div>
      </section>

      <section className="wx-wl-dsec">
        <h3>ขั้นตอนถัดไป (Next Actions)</h3>
        <div className="wx-wl-next-actions">
          {nextActions.length === 0 && <span className="wx-wl-next-action-info">ไม่มีขั้นตอนแนะนำเพิ่มเติม</span>}
          {nextActions.map((code) =>
            isNavigableNextAction(code) ? (
              <Link
                key={code}
                className="wx-wl-row-action wx-wl-row-action--full"
                to={
                  code === "CreatePlc"
                    ? onCreatePlc(detail.superAgentOrganizationId)
                    : onCreateAgent(detail.superAgentOrganizationId)
                }
              >
                {nextActionLabel(code)}
              </Link>
            ) : (
              <span key={code} className="wx-wl-next-action-info">
                {nextActionLabel(code)}
              </span>
            )
          )}
        </div>
        <p className="wx-wl-note-muted">
          <Link to={`/backoffice/super-agents/${detail.superAgentOrganizationId}`}>เปิดผังสายงานนี้ (Org Chart แบบเดิม)</Link>
        </p>
      </section>

      <div className="wx-wl-drawer-actions">
        <button type="button" className="wx-button wx-button--primary wx-wl-drawer-actions-full" onClick={onEdit}>
          แก้ไข Super Agent
        </button>
      </div>
    </div>
  );
}

const EMPTY_SUPER_AGENT_FORM = { displayName: "", legalName: "", ownerName: "", primaryPhoneNumber: "", lineId: "", note: "" };

export function emptySuperAgentForm() {
  return { ...EMPTY_SUPER_AGENT_FORM };
}

export function SuperAgentCreateView({ titleId, form, onChange, validationError, submitState, onSubmit }) {
  const submitting = submitState.status === "submitting";

  return (
    <div className="wx-wl-drawer">
      <span className="wx-wl-drawer-kicker">CREATE SUPER AGENT</span>
      <h2 id={titleId} className="wx-wl-drawer-title">
        สร้าง Super Agent
      </h2>
      <p className="wx-wl-drawer-sub">เพิ่มสายงานระดับบนสุดใหม่เข้าสู่ระบบ</p>

      <div className="wx-wl-notice">สร้าง/แก้ไขได้เฉพาะ SuperAdmin เท่านั้น ระบบจะปฏิเสธคำขอจากผู้ใช้อื่นโดยอัตโนมัติ</div>

      <label className="wx-wl-field">
        <span>
          {fieldLabel("displayName")} <b>*</b>
        </span>
        <input type="text" aria-label="displayName" placeholder="เช่น สายงานภาคเหนือ 03" value={form.displayName} onChange={(event) => onChange({ displayName: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>
          {fieldLabel("legalName")} <span className="wx-wl-optional">(ไม่บังคับ)</span>
        </span>
        <input type="text" aria-label="legalName" value={form.legalName} onChange={(event) => onChange({ legalName: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>{fieldLabel("ownerName")}</span>
        <input type="text" aria-label="ผู้ดูแล" value={form.ownerName} onChange={(event) => onChange({ ownerName: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>{fieldLabel("primaryPhoneNumber")}</span>
        <input type="tel" aria-label="primaryPhoneNumber" value={form.primaryPhoneNumber} onChange={(event) => onChange({ primaryPhoneNumber: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>{fieldLabel("lineId")}</span>
        <input type="text" aria-label="lineId" value={form.lineId} onChange={(event) => onChange({ lineId: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>
          {fieldLabel("note")} <span className="wx-wl-optional">(ไม่บังคับ)</span>
        </span>
        <textarea aria-label="note" value={form.note} onChange={(event) => onChange({ note: event.target.value })} />
      </label>

      {validationError && (
        <p className="wx-wl-form-message" role="alert">
          {validationError}
        </p>
      )}
      {submitState.status === "error" && (
        <p className="wx-wl-form-message" role="alert">
          {describeWriteError(submitState.error, "Super Agent นี้ถูกสร้างไปแล้ว กรุณาลองใหม่อีกครั้ง")}
        </p>
      )}

      <div className="wx-wl-drawer-actions">
        <button type="button" className="wx-button wx-button--primary wx-wl-drawer-actions-full" disabled={submitting} onClick={onSubmit}>
          {submitting ? "กำลังบันทึก..." : "บันทึก Super Agent"}
        </button>
      </div>
    </div>
  );
}

export function SuperAgentEditView({ titleId, code, form, onChange, validationError, submitState, onSubmit, onBack }) {
  const submitting = submitState.status === "submitting";

  return (
    <div className="wx-wl-drawer">
      <span className="wx-wl-drawer-kicker">EDIT SUPER AGENT</span>
      <h2 id={titleId} className="wx-wl-drawer-title">
        แก้ไข Super Agent
      </h2>
      <p className="wx-wl-drawer-sub">{code ?? "-"}</p>

      <label className="wx-wl-field">
        <span>
          {fieldLabel("displayName")} <b>*</b>
        </span>
        <input type="text" aria-label="displayName" value={form.displayName} onChange={(event) => onChange({ displayName: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>{fieldLabel("legalName")}</span>
        <input type="text" aria-label="legalName" value={form.legalName} onChange={(event) => onChange({ legalName: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>{fieldLabel("ownerName")}</span>
        <input type="text" aria-label="ผู้ดูแล" value={form.ownerName} onChange={(event) => onChange({ ownerName: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>{fieldLabel("primaryPhoneNumber")}</span>
        <input type="tel" aria-label="primaryPhoneNumber" value={form.primaryPhoneNumber} onChange={(event) => onChange({ primaryPhoneNumber: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>{fieldLabel("lineId")}</span>
        <input type="text" aria-label="lineId" value={form.lineId} onChange={(event) => onChange({ lineId: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>{fieldLabel("status")}</span>
        <select aria-label="status" value={form.status} onChange={(event) => onChange({ status: event.target.value })}>
          {WORKER_LINE_STATUS_OPTIONS.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </label>
      <label className="wx-wl-field">
        <span>
          {fieldLabel("note")} <span className="wx-wl-optional">(ไม่บังคับ)</span>
        </span>
        <textarea aria-label="note" value={form.note} onChange={(event) => onChange({ note: event.target.value })} />
      </label>

      {validationError && (
        <p className="wx-wl-form-message" role="alert">
          {validationError}
        </p>
      )}
      {submitState.status === "error" && (
        <p className="wx-wl-form-message" role="alert">
          {describeWriteError(submitState.error, "Super Agent นี้ถูกแก้ไขไปแล้วก่อนหน้านี้ กรุณารีเฟรชแล้วลองใหม่")}
        </p>
      )}

      <div className="wx-wl-drawer-actions">
        <button type="button" className="wx-button wx-button--ghost" onClick={onBack} disabled={submitting}>
          ย้อนกลับ
        </button>
        <button type="button" className="wx-button wx-button--primary" onClick={onSubmit} disabled={submitting}>
          {submitting ? "กำลังบันทึก..." : "บันทึกการแก้ไข"}
        </button>
      </div>
    </div>
  );
}
